import _ from "lodash";
import {PropsWithChildren} from "react";
import {ComposedChart, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis} from "recharts";
import {Box, Stack, Typography} from "@mui/material";
import {TimeSeriesDataProps} from "../../../types/timeSeriesDataProps";
import {BaseChartTooltip} from "./BaseChartTooltip";
import {BaseChartLegend} from "./BaseChartLegend";
import {FieldResult} from "../../../types/domain/fieldResult";
import dayjs from "dayjs";
import {HelpPopover} from "../../base/HelpPopover";

export type ChartComponent = React.FC<{
    data: FieldResult[];
}>;

export interface BaseChartProps extends PropsWithChildren {
    title: string;
    yLabel: string;
    data: TimeSeriesDataProps[];
    content?: React.ReactNode;
    yDomain?: [string, string];
    helpPopoverTranslationKey?: string;
}

export const BaseChart: React.FC<BaseChartProps> = ({
    title,
    yLabel,
    data,
    children,
    content,
    yDomain,
    helpPopoverTranslationKey,
}) => {
    return (
        <Stack
            direction="column"
            alignItems="stretch"
            bgcolor="#F3F5FA"
            padding="1.5rem"
            gap="1.25rem"
            border={1}
            borderColor="#E9EBF1 !important"
            position="relative"
        >
            <Typography textTransform="uppercase" fontSize={16} fontWeight={500} align="center">
                {title}
            </Typography>
            {content}
            <Box width="100%" height={430}>
                <ResponsiveContainer>
                    <ComposedChart data={data} syncId="insights_graph" margin={{bottom: 22, left: 10, right: 15}}>
                        {children}
                        <XAxis
                            dataKey="date"
                            angle={-45}
                            tickFormatter={formatXAxis}
                            dx={-18}
                            dy={18}
                            textDecoration="uppercase"
                            fontSize={14}
                        />
                        <YAxis
                            yAxisId="left"
                            domain={yDomain ?? ["auto", "auto"]}
                            fontSize={14}
                            label={{
                                value: yLabel,
                                angle: -90,
                                dx: -34,
                                textAnchor: "middle",
                            }}
                            tickFormatter={formatYAxis}
                        />
                        <Tooltip content={<BaseChartTooltip />} />
                        <Legend
                            align="right"
                            verticalAlign="top"
                            wrapperStyle={{paddingBottom: "1rem", paddingRight: "1rem"}}
                            content={<BaseChartLegend />}
                        />
                    </ComposedChart>
                </ResponsiveContainer>
            </Box>
            {helpPopoverTranslationKey !== undefined && helpPopoverTranslationKey?.length > 0 && (
                <Box sx={{position: "absolute", right: "0", top: "0"}}>
                    <HelpPopover i18nKey={helpPopoverTranslationKey} placement="top" direction="left" />
                </Box>
            )}
        </Stack>
    );
};

const formatXAxis = (tick: Date) => {
    return dayjs(tick).format("MMM D").toUpperCase();
};

const formatYAxis = (tick: number) => {
    return _.round(tick, 2).toString();
};
